import type {
  ProviderBeneficiary,
  ProviderBeneficiaryCompany,
  ProviderPayout,
  ProviderPayoutBeneficiaryParam,
  ProviderPayoutMethod,
  ProviderWallet,
  ProviderWalletAccount,
  ProviderWalletFundTransfer,
  ProviderWalletTransaction,
} from "@handshake/core-types";
import type { ProjectMilestone } from "../entities/project-milestone.entity";
import type { WalletOverviewDto, WalletTransactionRow } from "../dtos/wallet.dto";

function rec(raw: unknown): Record<string, unknown> {
  return raw != null && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
}

function str(r: Record<string, unknown>, c: string, s: string): string {
  const v = r[c] ?? r[s];
  return v != null ? String(v) : "";
}

function num(r: Record<string, unknown>, c: string, s: string): number {
  const v = r[c] ?? r[s];
  return typeof v === "number" && !Number.isNaN(v) ? v : Number(v) || 0;
}

function bool(r: Record<string, unknown>, c: string, s: string): boolean {
  const v = r[c] ?? r[s];
  return Boolean(v);
}

function unknownVal(r: Record<string, unknown>, c: string, s: string): unknown {
  return r[c] ?? r[s];
}

export function normalizeProviderWalletAccount(raw: unknown): ProviderWalletAccount {
  const a = rec(raw);
  return {
    walletAccountId: str(a, "walletAccountId", "wallet_account_id"),
    walletId: str(a, "walletId", "wallet_id"),
    orgId: str(a, "orgId", "org_id"),
    currency: str(a, "currency", "currency"),
    actualBalance: num(a, "actualBalance", "actual_balance"),
    availableBalance: num(a, "availableBalance", "available_balance"),
    isActive: bool(a, "isActive", "is_active"),
    isSettlementAllowed: bool(a, "isSettlementAllowed", "is_settlement_allowed"),
    isDeleted: bool(a, "isDeleted", "is_deleted"),
    createdAt: str(a, "createdAt", "created_at"),
    updatedAt: str(a, "updatedAt", "updated_at"),
    deletedAt: unknownVal(a, "deletedAt", "deleted_at"),
  };
}

export function normalizeProviderWallet(raw: unknown): ProviderWallet {
  const w = rec(raw);
  const accountsRaw = w["walletAccount"] ?? w["wallet_account"];
  const accounts = Array.isArray(accountsRaw) ? accountsRaw.map(normalizeProviderWalletAccount) : [];
  return {
    walletId: str(w, "walletId", "wallet_id"),
    walletAlias: str(w, "walletAlias", "wallet_alias"),
    orgId: str(w, "orgId", "org_id"),
    category: str(w, "category", "category"),
    scope: str(w, "scope", "scope"),
    customerId: str(w, "customerId", "customer_id"),
    description: str(w, "description", "description"),
    metadata: unknownVal(w, "metadata", "metadata"),
    isActive: bool(w, "isActive", "is_active"),
    isDeleted: bool(w, "isDeleted", "is_deleted"),
    webhookUrl: str(w, "webhookUrl", "webhook_url"),
    createdBy: str(w, "createdBy", "created_by"),
    createdAt: str(w, "createdAt", "created_at"),
    updatedAt: str(w, "updatedAt", "updated_at"),
    deletedAt: unknownVal(w, "deletedAt", "deleted_at"),
    walletAccount: accounts,
    inferredWalletType: (w["inferredWalletType"] ?? w["inferred_wallet_type"]) as ProviderWallet["inferredWalletType"],
  };
}

export function normalizeProviderWalletTransaction(raw: unknown, fallbackWalletId?: string): ProviderWalletTransaction {
  const t = rec(raw);
  return {
    walletTransactionId: str(t, "walletTransactionId", "wallet_transaction_id"),
    orgId: str(t, "orgId", "org_id"),
    walletId: str(t, "walletId", "wallet_id") || fallbackWalletId || "",
    walletAccountId: str(t, "walletAccountId", "wallet_account_id"),
    runningAvailableBalance: num(t, "runningAvailableBalance", "running_available_balance"),
    runningActualBalance: num(t, "runningActualBalance", "running_actual_balance"),
    type: str(t, "type", "type"),
    amount: num(t, "amount", "amount"),
    currency: str(t, "currency", "currency"),
    scope: str(t, "scope", "scope"),
    customerId: str(t, "customerId", "customer_id"),
    eventId: str(t, "eventId", "event_id"),
    eventType: str(t, "eventType", "event_type"),
    eventName: str(t, "eventName", "event_name"),
    eventDescription: str(t, "eventDescription", "event_description"),
    isToBeSettled: bool(t, "isToBeSettled", "is_to_be_settled"),
    toBeSettledAt: unknownVal(t, "toBeSettledAt", "to_be_settled_at"),
    isSettled: bool(t, "isSettled", "is_settled"),
    settledAt: unknownVal(t, "settledAt", "settled_at"),
    settlementStatus: str(t, "settlementStatus", "settlement_status"),
    settlementId: unknownVal(t, "settlementId", "settlement_id"),
    description: str(t, "description", "description"),
    invoiceId: unknownVal(t, "invoiceId", "invoice_id"),
    tag: unknownVal(t, "tag", "tag"),
    createdAt: str(t, "createdAt", "created_at"),
    updatedAt: str(t, "updatedAt", "updated_at"),
  };
}

export function normalizeProviderWalletFundTransfer(raw: unknown): ProviderWalletFundTransfer {
  const f = rec(raw);
  return {
    walletFundTransferId: str(f, "walletFundTransferId", "wallet_fund_transfer_id"),
    debitOrgId: str(f, "debitOrgId", "debit_org_id"),
    debitWalletId: str(f, "debitWalletId", "debit_wallet_id"),
    debitWalletAliasId: str(f, "debitWalletAliasId", "debit_wallet_alias_id"),
    debitCustomerId: unknownVal(f, "debitCustomerId", "debit_customer_id"),
    creditOrgId: str(f, "creditOrgId", "credit_org_id"),
    creditWalletId: str(f, "creditWalletId", "credit_wallet_id"),
    creditWalletAliasId: str(f, "creditWalletAliasId", "credit_wallet_alias_id"),
    creditCustomerId: str(f, "creditCustomerId", "credit_customer_id"),
    amount: num(f, "amount", "amount"),
    currency: str(f, "currency", "currency"),
    status: str(f, "status", "status"),
    description: str(f, "description", "description"),
    creditTransactionId: str(f, "creditTransactionId", "credit_transaction_id"),
    debitTransactionId: str(f, "debitTransactionId", "debit_transaction_id"),
    creditWalletTransactionId: str(f, "creditWalletTransactionId", "credit_wallet_transaction_id"),
    debitWalletTransactionId: str(f, "debitWalletTransactionId", "debit_wallet_transaction_id"),
    metadata: unknownVal(f, "metadata", "metadata"),
    additionalData: unknownVal(f, "additionalData", "additional_data"),
    createdAt: str(f, "createdAt", "created_at"),
    updatedAt: str(f, "updatedAt", "updated_at"),
  };
}

export function normalizeWalletTransactionRow(raw: unknown): WalletTransactionRow {
  const r = rec(raw);
  const base = normalizeProviderWalletTransaction(raw);
  const wfRaw = r["walletFundTransfer"] ?? r["wallet_fund_transfer"];
  const walletFundTransfer =
    wfRaw != null && typeof wfRaw === "object" ? normalizeProviderWalletFundTransfer(wfRaw) : undefined;
  return { ...base, walletFundTransfer };
}

function normalizePayoutBeneficiaryParam(raw: unknown): ProviderPayoutBeneficiaryParam {
  const p = rec(raw);
  return {
    type: str(p, "type", "type"),
    company: unknownVal(p, "company", "company"),
    currency: str(p, "currency", "currency"),
    bankName: str(p, "bankName", "bank_name"),
    bankCountry: str(p, "bankCountry", "bank_country"),
    accountNumber: str(p, "accountNumber", "account_number"),
  };
}

export function normalizeProviderPayout(raw: unknown): ProviderPayout {
  const p = rec(raw);
  const paramRaw = p["payoutBeneficiaryParam"] ?? p["payout_beneficiary_param"];
  return {
    payoutId: str(p, "payoutId", "payout_id"),
    payoutMethodName: str(p, "payoutMethodName", "payout_method_name"),
    payoutMethodParam: unknownVal(p, "payoutMethodParam", "payout_method_param"),
    orgId: str(p, "orgId", "org_id"),
    customerId: str(p, "customerId", "customer_id"),
    scope: str(p, "scope", "scope"),
    debitWalletId: str(p, "debitWalletId", "debit_wallet_id"),
    feesWalletId: str(p, "feesWalletId", "fees_wallet_id"),
    debitWalletTransactionId: str(p, "debitWalletTransactionId", "debit_wallet_transaction_id"),
    transactionId: str(p, "transactionId", "transaction_id"),
    feesWalletTransactionId: str(p, "feesWalletTransactionId", "fees_wallet_transaction_id"),
    beneficiaryAmount: num(p, "beneficiaryAmount", "beneficiary_amount"),
    beneficiaryCurrency: str(p, "beneficiaryCurrency", "beneficiary_currency"),
    beneficiaryCountry: str(p, "beneficiaryCountry", "beneficiary_country"),
    senderAmount: num(p, "senderAmount", "sender_amount"),
    senderCurrency: str(p, "senderCurrency", "sender_currency"),
    senderCountry: str(p, "senderCountry", "sender_country"),
    fxConversionId: unknownVal(p, "fxConversionId", "fx_conversion_id"),
    fxRateId: unknownVal(p, "fxRateId", "fx_rate_id"),
    fxRate: num(p, "fxRate", "fx_rate"),
    description: unknownVal(p, "description", "description"),
    purposeCode: str(p, "purposeCode", "purpose_code"),
    receiptEmail: unknownVal(p, "receiptEmail", "receipt_email"),
    payoutReference: unknownVal(p, "payoutReference", "payout_reference"),
    webhookUrl: unknownVal(p, "webhookUrl", "webhook_url"),
    status: str(p, "status", "status"),
    payoutType: str(p, "payoutType", "payout_type"),
    payoutBeneficiaryId: str(p, "payoutBeneficiaryId", "payout_beneficiary_id"),
    payoutBeneficiaryParam: paramRaw != null ? normalizePayoutBeneficiaryParam(paramRaw) : ({} as ProviderPayoutBeneficiaryParam),
    isPaid: bool(p, "isPaid", "is_paid"),
    paidAt: str(p, "paidAt", "paid_at"),
    paidAmount: num(p, "paidAmount", "paid_amount"),
    paidCurrency: str(p, "paidCurrency", "paid_currency"),
    isReturnable: bool(p, "isReturnable", "is_returnable"),
    isReturned: bool(p, "isReturned", "is_returned"),
    returnedAt: unknownVal(p, "returnedAt", "returned_at"),
    returnedAmount: unknownVal(p, "returnedAmount", "returned_amount"),
    returnedCurrency: unknownVal(p, "returnedCurrency", "returned_currency"),
    releasedAt: str(p, "releasedAt", "released_at"),
    feesWithoutTax: num(p, "feesWithoutTax", "fees_without_tax"),
    taxOnFees: unknownVal(p, "taxOnFees", "tax_on_fees"),
    feesIncludingTax: num(p, "feesIncludingTax", "fees_including_tax"),
    feesCurrency: str(p, "feesCurrency", "fees_currency"),
    isFeesCharged: bool(p, "isFeesCharged", "is_fees_charged"),
    isChargeable: bool(p, "isChargeable", "is_chargeable"),
    metadata: unknownVal(p, "metadata", "metadata"),
    additionalData: unknownVal(p, "additionalData", "additional_data"),
    organizationReferenceId: str(p, "organizationReferenceId", "organization_reference_id"),
    isReconciled: bool(p, "isReconciled", "is_reconciled"),
    createdAt: str(p, "createdAt", "created_at"),
    updatedAt: str(p, "updatedAt", "updated_at"),
    reasonForReturn: unknownVal(p, "reasonForReturn", "reason_for_return"),
    reasonForFailure: unknownVal(p, "reasonForFailure", "reason_for_failure"),
    bulkPayoutId: unknownVal(p, "bulkPayoutId", "bulk_payout_id"),
    pricingEventData: unknownVal(p, "pricingEventData", "pricing_event_data"),
  };
}

function normalizeBeneficiaryCompany(raw: unknown): ProviderBeneficiaryCompany {
  const c = rec(raw);
  return {
    name: str(c, "name", "name"),
    registrationNumber: unknownVal(c, "registrationNumber", "registration_number"),
    companyIncorporationCountry: unknownVal(c, "companyIncorporationCountry", "company_incorporation_country"),
    addressCountry: str(c, "addressCountry", "address_country"),
    addressState: str(c, "addressState", "address_state"),
    addressCity: str(c, "addressCity", "address_city"),
    addressLine1: str(c, "addressLine1", "address_line1"),
    addressLine2: str(c, "addressLine2", "address_line2"),
    addressZipCode: unknownVal(c, "addressZipCode", "address_zip_code"),
    phoneNumber: unknownVal(c, "phoneNumber", "phone_number"),
    phoneCountryCode: unknownVal(c, "phoneCountryCode", "phone_country_code"),
    phoneNumberE164: unknownVal(c, "phoneNumberE164", "phone_number_e164"),
    companyEmail: unknownVal(c, "companyEmail", "company_email"),
    identificationType: unknownVal(c, "identificationType", "identification_type"),
    identificationValue: unknownVal(c, "identificationValue", "identification_value"),
  };
}

export function normalizeProviderBeneficiary(raw: unknown): ProviderBeneficiary {
  const b = rec(raw);
  const companyRaw = b["company"];
  const company =
    companyRaw != null && typeof companyRaw === "object" ? normalizeBeneficiaryCompany(companyRaw) : undefined;
  return {
    payoutBeneficiaryId: str(b, "payoutBeneficiaryId", "payout_beneficiary_id"),
    orgId: str(b, "orgId", "org_id"),
    type: str(b, "type", "type"),
    beneficiaryName: str(b, "beneficiaryName", "beneficiary_name"),
    description: unknownVal(b, "description", "description"),
    metadata: unknownVal(b, "metadata", "metadata"),
    createdAt: str(b, "createdAt", "created_at"),
    updatedAt: str(b, "updatedAt", "updated_at"),
    isActive: bool(b, "isActive", "is_active"),
    currency: unknownVal(b, "currency", "currency"),
    bankCountry: unknownVal(b, "bankCountry", "bank_country"),
    bankState: unknownVal(b, "bankState", "bank_state"),
    bankCity: unknownVal(b, "bankCity", "bank_city"),
    bankName: str(b, "bankName", "bank_name"),
    nickname: unknownVal(b, "nickname", "nickname"),
    accountNumber: str(b, "accountNumber", "account_number"),
    bsb: unknownVal(b, "bsb", "bsb"),
    bicSwift: unknownVal(b, "bicSwift", "bic_swift"),
    intermediaryBicSwift: unknownVal(b, "intermediaryBicSwift", "intermediary_bic_swift"),
    iban: unknownVal(b, "iban", "iban"),
    ifsc: unknownVal(b, "ifsc", "ifsc"),
    bankCode: unknownVal(b, "bankCode", "bank_code"),
    branchCode: unknownVal(b, "branchCode", "branch_code"),
    aba: unknownVal(b, "aba", "aba"),
    sortCode: unknownVal(b, "sortCode", "sort_code"),
    payId: unknownVal(b, "payId", "pay_id"),
    payIdType: unknownVal(b, "payIdType", "pay_id_type"),
    proxyType: unknownVal(b, "proxyType", "proxy_type"),
    proxyValue: unknownVal(b, "proxyValue", "proxy_value"),
    organizationReferenceId: unknownVal(b, "organizationReferenceId", "organization_reference_id"),
    createdBy: str(b, "createdBy", "created_by"),
    additionalData: unknownVal(b, "additionalData", "additional_data"),
    company: company ?? {
      name: "",
      addressCountry: "",
      addressState: "",
      addressCity: "",
      addressLine1: "",
      addressLine2: "",
    },
  };
}

export function normalizeProviderPayoutMethod(raw: unknown): ProviderPayoutMethod {
  const m = rec(raw);
  return {
    payoutMethodId: str(m, "payoutMethodId", "payout_method_id"),
    payoutMethodName: str(m, "payoutMethodName", "payout_method_name"),
    payoutMethodDescription: str(m, "payoutMethodDescription", "payout_method_description"),
    payoutMethodLogo: str(m, "payoutMethodLogo", "payout_method_logo"),
    currency: str(m, "currency", "currency"),
    senderCountry: str(m, "senderCountry", "sender_country"),
    senderCurrency: str(m, "senderCurrency", "sender_currency"),
    beneficiaryCountry: str(m, "beneficiaryCountry", "beneficiary_country"),
    beneficiaryCurrency: str(m, "beneficiaryCurrency", "beneficiary_currency"),
    payoutMethodCategory: str(m, "payoutMethodCategory", "payout_method_category"),
    payoutMethodClass: str(m, "payoutMethodClass", "payout_method_class"),
    payoutMethodGroup: str(m, "payoutMethodGroup", "payout_method_group"),
    payoutMethodParam: unknownVal(m, "payoutMethodParam", "payout_method_param"),
    payoutBeneficiaryParam: unknownVal(m, "payoutBeneficiaryParam", "payout_beneficiary_param"),
    minAmount: num(m, "minAmount", "min_amount"),
    maxAmount: num(m, "maxAmount", "max_amount"),
    isReturnable: bool(m, "isReturnable", "is_returnable"),
    isContractRequired: bool(m, "isContractRequired", "is_contract_required"),
    isEnabledByDefault: bool(m, "isEnabledByDefault", "is_enabled_by_default"),
  };
}

export function normalizeWalletOverviewDto(raw: unknown): WalletOverviewDto {
  const o = rec(raw);
  const milestonesRaw = o["milestones"];
  const milestones = Array.isArray(milestonesRaw) ? (milestonesRaw as ProjectMilestone[]) : [];
  const staleRaw = o["staleAsOf"] ?? o["stale_as_of"];
  const actualWalletRaw = o["actualWallet"] ?? o["actual_wallet"];
  const escrowWalletRaw = o["escrowWallet"] ?? o["escrow_wallet"];
  const actualTxRaw = o["actualWalletTransactions"] ?? o["actual_wallet_transactions"];
  const escrowTxRaw = o["escrowWalletTransactions"] ?? o["escrow_wallet_transactions"];
  const actualTxList = Array.isArray(actualTxRaw) ? actualTxRaw : [];
  const escrowTxList = Array.isArray(escrowTxRaw) ? escrowTxRaw : [];
  return {
    actualWallet: normalizeProviderWallet(actualWalletRaw),
    escrowWallet: normalizeProviderWallet(escrowWalletRaw),
    milestones,
    actualWalletTransactions: actualTxList.map((x) =>
      normalizeProviderWalletTransaction(x, str(rec(actualWalletRaw), "walletId", "wallet_id"))
    ),
    escrowWalletTransactions: escrowTxList.map((x) =>
      normalizeProviderWalletTransaction(x, str(rec(escrowWalletRaw), "walletId", "wallet_id"))
    ),
    staleAsOf: staleRaw != null ? String(staleRaw) : undefined,
  };
}

export function normalizeProviderWalletList(raw: unknown): ProviderWallet[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeProviderWallet);
}

export function normalizeWalletTransactionList(raw: unknown): WalletTransactionRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeWalletTransactionRow);
}

export function normalizePayoutList(raw: unknown): ProviderPayout[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeProviderPayout);
}
